'use client';

import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { ExternalLink, Code, Smartphone, Globe, ChevronDown } from 'lucide-react';
import { motion, useInView } from 'motion/react';

import dynamic from 'next/dynamic';
const AnimatedBlobBackground = dynamic(
  () => import('./AnimatedBlobBackground').then((mod) => mod.AnimatedBlobBackground),
  { ssr: false },
);

import { LazyVideo } from './LazyVideo';
import { BeforeAfterSlider } from './BeforeAfterSlider';

// Reusable scroll animation wrapper component
function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
}: {
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '0px 0px -50px 0px' });

  const isMobile = useMediaQuery('(max-width: 768px)');

  const directions = {
    up: { y: 60, x: 0 },
    down: { y: -60, x: 0 },
    left: { x: isMobile ? 0 : 60, y: isMobile ? 60 : 0 },
    right: { x: isMobile ? 0 : -60, y: isMobile ? 60 : 0 },
  };

  return (
    <motion.div
      ref={ref}
      initial={{
        opacity: 0,
        ...directions[direction],
      }}
      animate={
        isInView
          ? {
            opacity: 1,
            x: 0,
            y: 0,
          }
          : {}
      }
      transition={{
        duration: 0.7,
        delay,
        ease: [0.21, 0.47, 0.32, 0.98],
      }}
    >
      {children}
    </motion.div>
  );
}

const faqs = [
  {
    question: 'How much does a custom website or mobile app cost?',
    answer:
      "At Zumify, we tailor our pricing based on the complexity and specific needs of your project. Whether you're a small business in Honolulu needing a simple landing page or a startup requiring a complex full-stack application, we provide transparent quotes that fit your budget and growth targets.",
  },
  {
    question: 'What is the typical timeline for a web design project in Hawaiʻi?',
    answer:
      "Most custom web design projects are completed within 3 to 8 weeks, depending on the scope. Mobile app development and complex AI integrations may vary depending on the scope of the project's needs. We prioritize quality and performance to ensure your digital product is ready to scale from day one.",
  },
  {
    question: 'Do you provide ongoing support after the project is launched?',
    answer:
      'Yes, we believe in long-term partnerships. We offer various maintenance and support packages to ensure your website or app remains secure, up-to-date, and optimized for search engines. Our local Hawaiʻi-based team is always just a phone call away.',
  },
  {
    question: 'How can AI solutions specifically help my small business?',
    answer:
      'Our AI services focus on workflow automation and intelligent agent integration. We help local businesses reduce operational costs by automating repetitive tasks, allowing you to focus on high-value strategy and customer engagement.',
  },
  {
    question: 'Why should I choose a local Hawaiʻi software development team?',
    answer:
      "Choosing a local team like Zumify means you have partners who understand the unique dynamics of the Hawaiʻi market. We can meet in person in Honolulu, align with your local brand values, and provide a level of personalized service that offshore agencies simply can't match.",
  },
];

function FAQItem({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border-b border-white/10">
      <button
        className="w-full py-6 flex items-center justify-between text-left hover:text-purple-400 transition-colors group"
        onClick={onClick}
      >
        <span className="text-xl sm:text-2xl font-semibold text-white group-hover:text-purple-300 transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-6 h-6 text-purple-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-stone-400 text-lg leading-relaxed">{answer}</p>
      </motion.div>
    </div>
  );
}

export function ResponsiveProjects() {
  const router = useRouter();
  const logynOkudaRef = useRef<HTMLHeadingElement>(null);
  const kpressLawRef = useRef<HTMLHeadingElement>(null);
  const nativeWebAppsRef = useRef<HTMLHeadingElement>(null);
  const shakaCardsRef = useRef<HTMLHeadingElement>(null);
  const whatsTheNameRef = useRef<HTMLHeadingElement>(null);
  const spibeRef = useRef<HTMLHeadingElement>(null);
  const prototypesRef = useRef<HTMLHeadingElement>(null);
  const hruRef = useRef<HTMLHeadingElement>(null);
  const hawaiiLoaCareRef = useRef<HTMLHeadingElement>(null);
  const waiolaRef = useRef<HTMLHeadingElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const projects = [
    {
      title: 'Whats The Name',
      subtitle: 'Name & Connection Flashcard App',
      description:
        'A custom iOS application built by Zumify demonstrating interactive flashcard workflows, strict on-device data privacy, and native in-app purchase architecture.',
      image: '/images/whats-the-name-banner.webp',
      tags: ['iOS App', 'Flashcards', 'App Store'],
      category: 'Mobile Application',
      icon: <Smartphone className="w-6 h-6" />,
    },
    {
      title: 'KPRESS Law',
      subtitle: 'Professional Law Firm Website',
      description:
        'A modern, SEO-optimized website for a Hawaiʻi-based law firm featuring professional design, strategic content structure, and enhanced search visibility to attract more clients.',
      image: '/images/KpressShowcase.webp',
      tags: ['Website', 'SEO', 'Professional'],
      category: 'Website Development',
      icon: <Globe className="w-6 h-6" />,
    },
    {
      title: 'Hawaii Rugby Union',
      subtitle: 'Non-Profit Rugby Website',
      description:
        'A new established website for the Hawaii Rugby Union, featuring tournament schedules, team management, and community engagement. Designed to unite and grow the rugby community across the islands.',
      image: '/images/HRUFeat.webp',
      tags: ['Non-Profit', 'Website', 'Rugby'],
      category: 'Website Development',
      icon: <Globe className="w-6 h-6" />,
    },
    {
      title: 'Hawaii Loa Care',
      subtitle: 'Hawaii Care Home Website Redesign',
      description:
        'A complete website transformation for a local Hawaiʻi Care Home provider—modernizing their digital presence with a clean, professional design optimized for trust and resident conversion.',
      image: '/images/HawaiiLoaCarePreview.webp',
      tags: ['Website', 'Healthcare', 'Redesign'],
      category: 'Website Development',
      icon: <Globe className="w-6 h-6" />,
    },
    {
      title: 'Waiola Shave Ice',
      subtitle: 'Modern Business Website',
      description:
        'A sleek, modern revamp website crafted to elevate the Waiola brand—featuring a refined fun aesthetic, a seamless user experience, and a digital presence built for capturing more customers attention on the love of shave ice.',
      image: '/images/WaiolaShaveIceHomepage.webp',
      tags: ['Website', 'Business', 'Modern'],
      category: 'Website Development',
      icon: <Globe className="w-6 h-6" />,
    },
    {
      title: 'Logyn Okuda',
      subtitle: 'Composer Portfolio Website',
      description:
        'A modern personal portfolio website for Logyn Okuda, a composer for film, television, and multimedia.',
      image: '/images/LogynPreview.webp',
      tags: ['Portfolio', 'Website', 'Composer'],
      category: 'Website Development',
      icon: <Globe className="w-6 h-6" />,
    },
    {
      title: 'Spibe',
      subtitle: 'Conversation Starter Platform',
      description:
        'A social platform matching personalities and traits to spark meaningful conversations. Features complete UI/UX design, multi-user interactions, and database.',
      image: '/images/SpibePreview.webp',
      tags: ['Full-Stack', 'Social', 'Beta'],
      category: 'Mobile Application',
      icon: <Smartphone className="w-6 h-6" />,
    },
    {
      title: 'App Prototypes',
      subtitle: 'Custom Application Designs',
      description:
        'Collection of custom prototypes including game-night trivia apps, social networking platforms, and career-planning tools. Each designed with user experience at the forefront.',
      image: '/images/prototypeDesign.webp',
      tags: ['Prototyping', 'Design', 'UX Research'],
      category: 'UI/UX Design',
      icon: <Code className="w-6 h-6" />,
    },
  ];

  const whatWeBuild = [
    {
      title: 'Websites',
      description: 'Modern, responsive websites focused on performance and SEO',
      features: ['SEO Optimized', 'Responsive Design', 'Quick Load Times'],
    },
    {
      title: 'Mobile Apps',
      description: 'Native and/or cross-platform apps for iOS and Android',
      features: ['Smooth Interactions', 'Cross-Platform', 'User-Friendly'],
    },
    {
      title: 'Prototypes',
      description: 'Interactive prototypes to visualize your ideas',
      features: ['Interactive', 'User Testing', 'Quick Iteration'],
    },
    {
      title: 'Full-Stack Apps',
      description: 'Complete applications with backend and database',
      features: ['Real-time Data', 'Secure Backend', 'Scalable'],
    },
    {
      title: 'UI/UX Design',
      description: 'Beautiful, user-centered interface and experience design',
      features: ['User Research', 'Wireframing', 'Visual Design'],
    },
    {
      title: 'AI Agents',
      description: 'Exquisitely engineered AI agents designed for real-world impact',
      features: ['Workflow Automation', 'Agent Strategy & Design', 'AI Integration'],
    },
  ];

  const scrollToSection = (ref: React.RefObject<HTMLElement>) => {
    if (ref.current) {
      const headerOffset = 20;
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const scrollToLogynOkuda = () => scrollToSection(logynOkudaRef);
  const scrollToKPRESSLaw = () => scrollToSection(kpressLawRef);
  const scrollToShakaCards = () => scrollToSection(shakaCardsRef);
  const scrollToWhatsTheName = () => scrollToSection(whatsTheNameRef);
  const scrollToSpibe = () => scrollToSection(spibeRef);
  const scrollToPrototypes = () => scrollToSection(prototypesRef);
  const scrollToHRU = () => scrollToSection(hruRef);
  const scrollToHawaiiLoaCare = () => scrollToSection(hawaiiLoaCareRef);
  const scrollToWaiola = () => scrollToSection(waiolaRef);

  const pulse = {
    opacity: [0.3, 0.6, 0.3],
    scale: [1, 1.1, 1],
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' as const },
  };

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col relative overflow-hidden">
      {/* Animated Blob Background with scroll-based colors */}
      <AnimatedBlobBackground enableScrollColors={true} blobCount={8} />

      <div className="relative z-10">
        <ResponsiveHeader />

        {/* Hero Section */}
        <section className="px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-32 pb-4 lg:pb-8 relative overflow-hidden">
          {/* Background Animation */}
          <div className="absolute inset-0 pointer-events-none">
            <motion.div
              className="absolute top-20 left-10 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl opacity-30"
              animate={pulse}
            />
            <motion.div
              className="absolute bottom-20 right-10 w-96 h-96 bg-pink-600/10 rounded-full blur-3xl opacity-30"
              animate={pulse}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1,
              }}
            />
          </div>

          <div className="max-w-7xl mx-auto text-center relative z-10">
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 pt-8 sm:pt-12 lg:pt-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              Hawaiʻi based software applications and{' '}
              <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                Projects
              </span>
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl lg:text-2xl text-stone-300 max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            >
              Explore our portfolio of innovative digital solutions crafted for our clients
            </motion.p>
          </div>
        </section>

        {/* What We Build Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center text-white mb-12">
                Custom Software That Fits Your Business
              </h2>
            </ScrollReveal>
            <div className="space-y-6 lg:space-y-0">
              {/* Mobile: Single column, Tablet: 2 columns, Desktop: 3 columns (2 rows of 3) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {whatWeBuild.map((service, i) => {
                  const glowColors = [
                    {
                      border: 'rgba(34, 211, 238, 1)',
                      shadow:
                        '0 0 20px rgba(34, 211, 238, 0.6), 0 0 40px rgba(34, 211, 238, 0.4), 0 0 60px rgba(34, 211, 238, 0.2)',
                    }, // Cyan
                    {
                      border: 'rgba(16, 185, 129, 1)',
                      shadow:
                        '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.4), 0 0 60px rgba(16, 185, 129, 0.2)',
                    }, // Emerald
                    {
                      border: 'rgba(251, 146, 60, 1)',
                      shadow:
                        '0 0 20px rgba(251, 146, 60, 0.6), 0 0 40px rgba(251, 146, 60, 0.4), 0 0 60px rgba(251, 146, 60, 0.2)',
                    }, // Orange
                    {
                      border: 'rgba(236, 72, 153, 1)',
                      shadow:
                        '0 0 20px rgba(236, 72, 153, 0.6), 0 0 40px rgba(236, 72, 153, 0.4), 0 0 60px rgba(236, 72, 153, 0.2)',
                    }, // Pink
                    {
                      border: 'rgba(139, 92, 246, 1)',
                      shadow:
                        '0 0 20px rgba(139, 92, 246, 0.6), 0 0 40px rgba(139, 92, 246, 0.4), 0 0 60px rgba(139, 92, 246, 0.2)',
                    }, // Violet
                    {
                      border: 'rgba(59, 130, 246, 1)',
                      shadow:
                        '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2)',
                    }, // Blue
                  ];

                  return (
                    <ScrollReveal key={i} delay={i * 0.15} direction="up">
                      <div
                        className="group p-6 lg:p-8 bg-zinc-800/50 backdrop-blur-sm border-2 rounded-3xl transition-all duration-500 hover:scale-105 h-full relative"
                        style={{
                          borderColor: 'rgba(147, 51, 234, 0.2)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = glowColors[i].border;
                          e.currentTarget.style.boxShadow = glowColors[i].shadow;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.2)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <h3 className="text-xl lg:text-2xl font-semibold text-white mb-3">
                          {service.title}
                        </h3>
                        <p className="text-stone-300 mb-4">{service.description}</p>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="text-sm text-stone-400">
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </ScrollReveal>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Projects Grid */}
        <section className="px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
              {projects.map((project, index) => {
                // Determine which scroll function to use based on project title
                let scrollFunction = scrollToKPRESSLaw;
                if (project.title === 'Logyn Okuda') {
                  scrollFunction = scrollToLogynOkuda;
                } else if (project.title === 'Whats The Name') {
                  scrollFunction = scrollToWhatsTheName;
                } else if (project.title === 'ShakaCards') {
                  scrollFunction = scrollToShakaCards;
                } else if (project.title === 'Spibe') {
                  scrollFunction = scrollToSpibe;
                } else if (project.title === 'App Prototypes') {
                  scrollFunction = scrollToPrototypes;
                } else if (project.title === 'Hawaii Rugby Union') {
                  scrollFunction = scrollToHRU;
                } else if (project.title === 'Hawaii Loa Care') {
                  scrollFunction = scrollToHawaiiLoaCare;
                } else if (project.title === 'Waiola Shave Ice') {
                  scrollFunction = scrollToWaiola;
                }

                return (
                  <ScrollReveal
                    key={index}
                    direction={index % 2 === 0 ? 'left' : 'right'}
                    delay={index * 0.1}
                  >
                    <div
                      className="group bg-zinc-800/50 backdrop-blur-sm border-2 border-purple-600/20 rounded-3xl overflow-hidden hover:border-purple-600 transition-all duration-300 hover:scale-[1.02] h-full cursor-pointer"
                      onClick={scrollFunction}
                    >
                      {/* Project Image */}
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={project.image}
                          alt={`${project.title} ${project.subtitle}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent opacity-60" />

                        {/* Category Badge */}
                        <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/80 backdrop-blur-sm px-4 py-2 rounded-full border border-purple-600/50">
                          <div className="text-purple-400">{project.icon}</div>
                          <span className="text-white text-sm font-semibold">
                            {project.category}
                          </span>
                        </div>
                      </div>

                      {/* Project Info */}
                      <div className="p-6 sm:p-8">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                              {project.title}
                            </h3>
                            <p className="text-purple-400 text-sm sm:text-base mb-3">
                              {project.subtitle}
                            </p>
                          </div>
                          <button
                            className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 flex-shrink-0 ml-4"
                            aria-label="View project"
                          >
                            <ExternalLink className="w-5 h-5 text-white" />
                          </button>
                        </div>

                        <p className="text-stone-300 text-base sm:text-lg mb-6 leading-relaxed">
                          {project.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {project.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="px-3 py-1 bg-purple-600/20 border border-purple-600/30 text-purple-300 rounded-full text-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Website Showcase Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-zinc-800/30">
          <div className="max-w-7xl mx-auto">
            {/* Glassmorphic Container */}
            <div className="relative bg-white/5 backdrop-blur-xl border-2 border-white/10 rounded-3xl lg:rounded-[51px] p-6 sm:p-8 lg:p-12 overflow-hidden">
              <ScrollReveal>
                <h2
                  data-section="websites"
                  className="text-3xl sm:text-4xl lg:text-5xl text-center text-white mb-8 lg:mb-12 font-bold"
                >
                  Websites we have built for local businesses
                </h2>
              </ScrollReveal>

              {/* KPRESS Law Section */}
              <div className="mt-24 lg:mt-16">
                <ScrollReveal>
                  <h3
                    ref={kpressLawRef}
                    className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4"
                  >
                    <a
                      href="https://www.kpresslaw.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-purple-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                    >
                      KPRESS Law Hawaiʻi
                      <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                    </a>
                  </h3>
                  <p className="text-purple-400 text-base sm:text-lg mb-8">
                    Professional Law Firm Website
                  </p>
                </ScrollReveal>

                {/* Video Showcase */}
                <ScrollReveal delay={0.2}>
                  <div className="relative w-full aspect-video rounded-lg lg:rounded-[9px] overflow-hidden mb-8 lg:mb-12 shadow-2xl">
                    <LazyVideo
                      src="/videos/kpress.mp4"
                    poster="/images/KpressShowcase.webp"
                    className="absolute inset-0 w-full h-full bg-zinc-900"
                    videoClassName="object-cover"
                    style={{
                      opacity: 1,
                      visibility: 'visible',
                      aspectRatio: '16/9',
                    }}
                  />
                </div>
              </ScrollReveal>

              {/* Content Layout */}
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 xl:gap-10 items-start">
                {/* Text Content */}
                <ScrollReveal direction="left" delay={0.3}>
                  <div className="flex-1 space-y-6 text-white">
                    <div className="space-y-4 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-stone-200">
                      {/* The Challenge Section */}
                      <div className="mb-8">
                        <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                          <div className="w-8 h-px bg-gradient-to-r from-purple-500 to-cyan-500" />
                          The Challenge
                        </h4>
                        <ul className="space-y-4">
                          {[
                            {
                              title: 'Outdated Digital Presence',
                              description:
                                'The site before was confusing, simple, and not mobile friendly.',
                            },
                            {
                              title: 'Needed more qualified leads',
                              description: "Visitor traffic wasn't translating into strong leads.",
                            },
                            {
                              title: 'Performance Issues',
                              description:
                                'Slow load times and a confusing landing page were causing potential clients to bounce.',
                            },
                          ].map((item, index) => (
                            <li key={index} className="flex items-start gap-3 group">
                              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                              <p className="text-stone-300 leading-relaxed text-base sm:text-lg lg:text-xl">
                                <strong className="text-white group-hover:text-purple-300 transition-colors duration-300">
                                  {item.title}:
                                </strong>{' '}
                                {item.description}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* The Solution Section */}
                      <div>
                        <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                          <div className="w-8 h-px bg-gradient-to-r from-cyan-500 to-purple-500" />
                          The Solution
                        </h4>
                      </div>

                      {/* Original Description Text */}
                      <p>
                        The solution of this project helped our client create an effective,
                        strategic website built to compete in the probate legal industry. With SEO
                        strategy, and making the site the experience, keeping users attracted and
                        staying on the site, is what makes it important.
                      </p>

                      <p>Our approach blends aesthetic excellence with data-backed marketing.</p>

                      <p>
                        We craft clear, credible brand identities, build fast and intuitive
                        websites, and structure content to rank higher in search results. From local
                        visibility to national reach, we focus on elevating your digital presence so
                        your ideal clients find you first.
                      </p>

                      <p>
                        At Zumify, design isn't just about visuals—it's about results. Every project
                        is tailored to your practice's personality, goals, and growth targets,
                        giving you a competitive edge in a crowded marketplace.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-white/10 mt-8"></div>
                </ScrollReveal>

                {/* Image Showcase - Responsive: Below text on mobile/tablet, side-by-side on desktop */}
                <ScrollReveal direction="up" delay={0.4}>
                  <div className="w-full sm:max-w-sm md:max-w-md mx-auto lg:mx-0 lg:flex-shrink-0 lg:w-64 xl:w-80 2xl:w-96">
                    <div className="relative w-full aspect-[533/1361] rounded-2xl lg:rounded-[26px] overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-500">
                      <img
                        src="/images/f7fc4732d2302ed582b92482abb3c55d08f3e410.webp"
                        alt="KPRESS Law Website Showcase"
                        className="w-full h-full object-cover object-center"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </ScrollReveal>
              </div>
              </div>

              {/* HRU Project Section */}
              <div className="mt-24 lg:mt-32">
                <ScrollReveal>
                  <h3
                    ref={hruRef}
                    className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4"
                  >
                    <a
                      href="https://hawaiirugbyunion.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-purple-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                    >
                      Hawaii Rugby Union
                      <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                    </a>
                  </h3>
                  <p className="text-purple-400 text-base sm:text-lg mb-8">
                    Non-Profit Rugby Website
                  </p>
                </ScrollReveal>

                {/* Video Showcase */}
                <ScrollReveal delay={0.2}>
                  <div className="relative w-full aspect-video rounded-lg lg:rounded-[9px] overflow-hidden mb-8 lg:mb-12 shadow-2xl">
                    <LazyVideo
                      src="/videos/HRUDesktop.mp4"
                      poster="/images/HRUFeat.webp"
                      className="absolute inset-0 w-full h-full bg-zinc-900"
                      videoClassName="object-cover"
                      style={{
                        opacity: 1,
                        visibility: 'visible',
                        aspectRatio: '16/9',
                      }}
                    />
                  </div>
                </ScrollReveal>

                {/* Content Layout */}
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 xl:gap-10 items-start">
                  {/* Text Content */}
                  <ScrollReveal direction="left" delay={0.3}>
                    <div className="flex-1 space-y-6 text-white">
                      <div className="space-y-4 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-stone-200">
                        <p>
                          The Hawaii Rugby Union is dedicated to growing the sport of rugby across
                          the islands. We partnered with them to create a digital platform that
                          connects players, coaches, and fans, fostering a strong and inclusive
                          community.
                        </p>

                        <p>
                          The new website serves as a central hub for league schedules, team
                          information, and union resources. We focused on delivering a user-friendly
                          experience that makes critical information accessible to everyone.
                        </p>

                        <p>
                          By combining modern web technologies with the spirit of aloha, we built a
                          site that not only looks great but also strengthens the foundation of
                          rugby in Hawaii for generations to come.
                        </p>
                      </div>
                    </div>
                  </ScrollReveal>
                </div>

                {/* HRU Reviews/Comments Grid */}
                <div className="mt-16 lg:mt-20">
                  <ScrollReveal>
                    <h4 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-white mb-8 lg:mb-12">
                      What the Community is Saying
                    </h4>
                  </ScrollReveal>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {[
                      {
                        name: 'Jerry Razor',
                        role: 'Sponsor',
                        content: 'Wow Mafu, Awesome ! First class!!',
                      },
                      {
                        name: 'Kalei Hanawahine',
                        role: 'Sports Travel Hawaii',
                        content: 'Sweet!!',
                      },
                      {
                        name: 'Luke Campilo',
                        role: 'President of Titans RFC',
                        content: 'Thats lookin pretty slick!',
                      },
                    ].map((review, i) => (
                      <ScrollReveal key={i} delay={i * 0.15} direction="up">
                        <div className="bg-zinc-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-zinc-800/70 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col relative group overflow-hidden shadow-lg">
                          {/* Decorative elements */}
                          <div className="absolute top-1/2 -right-10 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl -translate-y-1/2 group-hover:bg-purple-500/20 transition-colors duration-500" />

                          <div className="flex items-center gap-4 mb-4 relative z-10 transition-transform duration-300 group-hover:translate-x-1">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0 text-white font-bold text-sm shadow-md">
                              {review.name.charAt(0)}
                            </div>
                            <div>
                              <div className="font-semibold text-white leading-tight">
                                {review.name}
                              </div>
                              <div className="text-xs text-purple-400 font-medium">
                                {review.role}
                              </div>
                            </div>
                          </div>

                          <div className="relative z-10 flex-1 bg-zinc-900 rounded-xl p-4 border border-white/5">
                            {/* Message tail */}
                            <div className="absolute top-4 -left-2 w-4 h-4 bg-zinc-900 border-l border-b border-white/5 transform rotate-45" />

                            <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
                              "{review.content}"
                            </p>
                          </div>
                        </div>
                      </ScrollReveal>
                    ))}
                  </div>
                </div>
              </div>

              {/* Hawaii Loa Care Section */}
              <div className="mt-24 lg:mt-16">
                <ScrollReveal>
                  <h3
                    ref={hawaiiLoaCareRef}
                    className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4"
                  >
                    <a
                      href="https://hawaiiloacare.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-purple-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                    >
                      Hawaii Loa Care
                      <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                    </a>
                  </h3>
                  <p className="text-purple-400 text-base sm:text-lg mb-4">
                    Hawaii Care Home Website Redesign
                  </p>
                  <p className="text-stone-300 text-sm sm:text-base italic mb-8">
                    Slide the bar left and right to see the before and after transformation.
                  </p>
                </ScrollReveal>

                {/* Before & After Video Comparison — drag slider */}
                <ScrollReveal delay={0.2}>
                  <div className="mb-10">
                    <BeforeAfterSlider
                      beforeSrc="/videos/HawaiiLoaCareBefore_small.mp4"
                      afterSrc="/videos/HawaiiLoaCareAfter_small.mp4"
                      poster="/images/HawaiiLoaCarePreview.webp"
                    />
                  </div>
                </ScrollReveal>

                {/* Content Layout */}
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 xl:gap-10 items-start">
                  <ScrollReveal direction="left" delay={0.3}>
                    <div className="flex-1 space-y-6 text-white">
                      <div className="space-y-4 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-stone-200">
                        {/* The Challenge Section */}
                        <div className="mb-8">
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <div className="w-8 h-px bg-gradient-to-r from-purple-500 to-cyan-500" />
                            The Challenge
                          </h4>
                          <ul className="space-y-4">
                            {[
                              {
                                title: 'Outdated, Untrustworthy Design',
                                description:
                                  'The original site felt dated and had nonrelated blogs from a their old site.',
                              },
                              {
                                title: 'Poor Mobile Experience',
                                description:
                                  'The site was not mobile-friendly, causing high bounce rates from smartphone users looking for care.',
                              },
                              {
                                title: 'Low Patient Activity',
                                description:
                                  'Visitors struggled to find key information quickly, leading to lost appointment opportunities.',
                              },
                            ].map((item, index) => (
                              <li key={index} className="flex items-start gap-3 group">
                                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                                <p className="text-stone-300 leading-relaxed text-base sm:text-lg lg:text-xl">
                                  <strong className="text-white group-hover:text-purple-300 transition-colors duration-300">
                                    {item.title}:
                                  </strong>{' '}
                                  {item.description}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* The Solution Section */}
                        <div>
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <div className="w-8 h-px bg-gradient-to-r from-cyan-500 to-purple-500" />
                            The Solution
                          </h4>
                          <p className="text-stone-300 leading-relaxed">
                            We completely reimagined Hawaii Loa Care's digital presence—starting
                            from scratch with a clean, modern design that builds immediate trust.
                            The new site is fully responsive, fast-loading, and structured around
                            clear patient pathways that guide visitors toward booking care.
                          </p>
                          <p className="text-stone-300 leading-relaxed mt-4">
                            By combining compassionate visual design with strategic content layout
                            and local SEO, the new website positions Hawaii Loa Care as a premium,
                            trustworthy care home provider in the community.
                          </p>
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              </div>

              {/* Waiola Section */}
              <div className="mt-24 lg:mt-16">
                <ScrollReveal>
                  <h3
                    ref={waiolaRef}
                    className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4"
                  >
                    <a
                      href="https://www.waiolashaveice.co/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-purple-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                    >
                      Waiola Shave Ice
                      <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                    </a>
                  </h3>
                  <p className="text-purple-400 text-base sm:text-lg mb-8">
                    Family Friendly Website Revamp
                  </p>
                </ScrollReveal>

                {/* Video Showcase */}
                <ScrollReveal delay={0.2}>
                  <div className="relative w-full aspect-video rounded-lg lg:rounded-[9px] overflow-hidden mb-8 lg:mb-12 shadow-2xl">
                    <LazyVideo
                      src="/videos/WaiolaPreview.mp4"
                      poster="/images/WaiolaShaveIceHomepage.webp"
                      className="absolute inset-0 w-full h-full bg-zinc-900"
                      videoClassName="object-cover"
                      style={{
                        opacity: 1,
                        visibility: 'visible',
                        aspectRatio: '16/9',
                      }}
                    />
                  </div>
                </ScrollReveal>

                {/* Content Layout */}
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 xl:gap-10 items-start">
                  <ScrollReveal direction="left" delay={0.3}>
                    <div className="flex-1 space-y-6 text-white">
                      <div className="space-y-4 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-stone-200">
                        {/* The Challenge Section */}
                        <div className="mb-8">
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <div className="w-8 h-px bg-gradient-to-r from-purple-500 to-cyan-500" />
                            The Challenge
                          </h4>
                          <ul className="space-y-4">
                            {[
                              {
                                title: 'Outdated, disorganized, and not doing too much online',
                                description:
                                  'Waiola needed a modern website that accurately reflected its family-friendly atmosphere and iconic local brand, creating an online experience that feels as welcoming and memorable as visiting in person.',
                              },
                              {
                                title: 'Brand Identity Alignment',
                                description:
                                  "The design needed to reflect Waiola's unique character and values—clean, modern, and memorable.",
                              },
                              {
                                title: 'Conversion-Focused Experience',
                                description:
                                  'Each page was built with clear conversion paths that naturally lead visitors to inquire about Waiola’s services, book catering opportunities, and drive more foot traffic to their stores.',
                              },
                            ].map((item, index) => (
                              <li key={index} className="flex items-start gap-3 group">
                                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                                <p className="text-stone-300 leading-relaxed text-base sm:text-lg lg:text-xl">
                                  <strong className="text-white group-hover:text-purple-300 transition-colors duration-300">
                                    {item.title}:
                                  </strong>{' '}
                                  {item.description}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* The Solution Section */}
                        <div>
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <div className="w-8 h-px bg-gradient-to-r from-cyan-500 to-purple-500" />
                            The Solution
                          </h4>
                          <p className="text-stone-300 leading-relaxed">
                            We redesigned Waiola’s website with a modern, visually engaging
                            experience that creates a strong first impression and encourages
                            visitors to become customers. Built around Waiola’s iconic shave ice
                            offerings and family-friendly atmosphere, the site combines compelling
                            design with performance-focused development to deliver a digital
                            presence that authentically reflects the brand and its island heritage.
                          </p>
                          <p className="text-stone-300 leading-relaxed mt-4">
                            From compelling visuals to strategic content flow, every element was
                            designed to build trust, showcase value, and drive meaningful engagement
                            for long-term growth.
                          </p>
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              </div>

                         {/* Logyn Okuda Section */}
              <div className="mt-24 lg:mt-16">
                <ScrollReveal>
                  <h3
                    ref={logynOkudaRef}
                    className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4"
                  >
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-purple-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                    >
                      Logyn Okuda
                      <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                    </a>
                  </h3>
                  <p className="text-purple-400 text-base sm:text-lg mb-8">
                    Composer Portfolio Website
                  </p>
                </ScrollReveal>

                {/* Video Showcase */}
                <ScrollReveal delay={0.2}>
                  <div className="relative w-full aspect-video rounded-lg lg:rounded-[9px] overflow-hidden mb-8 lg:mb-12 shadow-2xl">
                    <LazyVideo
                      src="/videos/LogynOkudaVideo.mp4"
                      poster="/images/LogynPreview.webp"
                      className="absolute inset-0 w-full h-full bg-zinc-900"
                      videoClassName="object-cover"
                      style={{
                        opacity: 1,
                        visibility: 'visible',
                        aspectRatio: '16/9',
                      }}
                    />
                  </div>
                </ScrollReveal>

                {/* Content Layout */}
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 xl:gap-10 items-start">
                  <ScrollReveal direction="left" delay={0.3}>
                    <div className="flex-1 space-y-6 text-white">
                      <div className="space-y-4 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-stone-200">
                        {/* The Challenge Section */}
                        <div className="mb-8">
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <div className="w-8 h-px bg-gradient-to-r from-purple-500 to-cyan-500" />
                            The Challenge
                          </h4>
                              <ul className="space-y-4">
                          {[
                            {
                              title: 'Sqaurespace Template was too basic, nothing special',
                              description:
                                'The site before was too simple, basic white format, buttons werent navigating properly, and not mobile friendly.',
                            },
                            {
                              title: 'Wasnt up to par with other composers in LA',
                              description: "Searching up Michael Giacchino, Jeff Kryka, and others, they have a unique formatting style that represented them, Logyn's site didn't represent him as a composer.",
                            },
                            {
                              title: 'Wasnt assessible to navigate and see his work',
                              description:
                                'Many issues with all his work not being organized properly, hard to get details on each project he worked on.',
                            },
                          ].map((item, index) => (
                            <li key={index} className="flex items-start gap-3 group">
                              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                              <p className="text-stone-300 leading-relaxed text-base sm:text-lg lg:text-xl">
                                <strong className="text-white group-hover:text-purple-300 transition-colors duration-300">
                                  {item.title}:
                                </strong>{' '}
                                {item.description}
                              </p>
                            </li>
                          ))}
                        </ul>
                        </div>

                        {/* The Solution Section */}
                        <div>
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <div className="w-8 h-px bg-gradient-to-r from-cyan-500 to-purple-500" />
                            The Solution
                          </h4>
                          <div className="text-stone-300 leading-relaxed space-y-4">
                            <p>
                              The client wanted a website that felt professional and modern without being overly
                              complex or flashy. Working closely with him, we developed a portfolio experience
                              tailored to his needs, with a strong focus on accessibility, intuitive navigation,
                              and making his work easy to discover. Visitors can seamlessly explore his portfolio
                              and listen to his music through multiple platforms and embedded media.
                            </p>
                            
                            <p>
                              Zumify integrated Apple Music, YouTube, and custom audio recordings featuring his
                              work across film, television, and original compositions. We also organized his
                              composition and orchestration credits into a streamlined portfolio that makes his
                              experience easy to discover.
                            </p>

                            <p>
                              Our goal was to create a website that felt uniquely his while modernizing how his work is presented,
                              helping him showcase his expertise and connect with new collaborators.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>

                  {/* Mobile Video Showcase */}
                  <ScrollReveal direction="up" delay={0.4}>
                    <div className="w-full sm:max-w-sm md:max-w-md mx-auto lg:mx-0 lg:flex-shrink-0 lg:w-64 xl:w-80 2xl:w-96">
                      <div className="relative w-full aspect-[9/19] rounded-[40px] lg:rounded-[65px] overflow-hidden shadow-2xl border-2 border-purple-500/20 hover:scale-105 transition-transform duration-500">
                        <LazyVideo
                          src="/videos/LogynMobile.MP4"
                          poster="/images/LogynPreview.webp"
                          className="absolute inset-0 w-full h-full bg-zinc-900"
                          videoClassName="object-cover object-center"
                        />
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* App Showcases Section */}
        <section
          className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-zinc-800/30"
          data-section="native-web-apps"
        >
          <div className="max-w-7xl mx-auto">
            {/* Glassmorphic Container */}
            <div className="relative bg-white/5 backdrop-blur-xl border-2 border-white/10 rounded-3xl lg:rounded-[51px] p-6 sm:p-8 lg:p-12 xl:p-16">
              <ScrollReveal>
                <h2
                  ref={nativeWebAppsRef}
                  data-section="native-web-apps"
                  className="text-3xl sm:text-4xl lg:text-5xl text-center text-white mb-12 lg:mb-16 font-bold"
                >
                  Mobile Apps Developed in Hawaiʻi
                </h2>
              </ScrollReveal>

              {/* WhatsTheName Showcase */}
              <div className="mb-16 lg:mb-24">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start">
                  {/* Text Content */}
                  <ScrollReveal direction="left">
                    <div className="flex-1 space-y-6">
                      <h3
                        ref={whatsTheNameRef}
                        className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-white font-bold"
                      >
                        Whats The Name
                      </h3>

                      <div className="space-y-4 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-stone-200">
                        <p>
                          Whats The Name demonstrates Zumify&apos;s capabilities in custom native mobile app development. Built as a published iOS application, it showcases how we engineer intuitive flashcard interfaces, custom user management, and seamless mobile experiences tailored to real-world business needs.
                        </p>

                        <p>
                          Our team guided Whats The Name through the entire production pipeline—from initial UI/UX wireframing in Figma to full native iOS development, RevenueCat billing integration, and App Store publication. Whether you need a custom training app, client relationship tool, or community platform, we can build a similar custom solution tailored to your organization.
                        </p>

                        {/* Feature Highlights Bullet List (The Challenge Style) */}
                        <div className="pt-2">
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <div className="w-8 h-px bg-gradient-to-r from-purple-500 to-cyan-500" />
                            Key Capabilities & Architecture
                          </h4>
                          <ul className="space-y-4">
                            {[
                              {
                                title: 'Interactive Memory Flashcards',
                                description:
                                  'Demonstrating custom interactive UI workflows that pair visual media, names, and context to maximize user retention and engagement.',
                              },
                              {
                                title: '100% On-Device Data Privacy',
                                description:
                                  'Architected with strict local data processing—ensuring sensitive customer photos are stored 100% locally on the device without requiring external cloud servers.',
                              },
                              {
                                title: 'In-app Purchases & Custom Folders',
                                description:
                                  'Created an integrated in-app purchase system, allowing users to be able to unlock premium features such as custom folders.',
                              },
                            ].map((item, index) => (
                              <li key={index} className="flex items-start gap-3 group">
                                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                                <p className="text-stone-300 leading-relaxed text-base sm:text-lg lg:text-xl">
                                  <strong className="text-white group-hover:text-purple-300 transition-colors duration-300">
                                    {item.title}:
                                  </strong>{' '}
                                  {item.description}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row items-center gap-3 pt-4">
                        <a
                          href="https://apps.apple.com/eg/app/whatsthename/id6773097490"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold text-sm sm:text-base transition-all shadow-lg shadow-purple-600/30 hover:scale-105"
                        >
                          <ExternalLink className="w-5 h-5" />
                          View on App Store
                        </a>
                        <a
                          href="/whatsTheName/privacy"
                          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-zinc-800/80 hover:bg-zinc-800 border border-white/10 text-stone-300 hover:text-white font-medium text-sm transition-all hover:scale-105"
                        >
                          Privacy Policy
                        </a>
                      </div>
                    </div>
                  </ScrollReveal>

                  {/* Phone Showcase Container - App Store Screenshot (Right Side) */}
                  <div className="flex-shrink-0 w-full sm:w-80 lg:w-96 xl:w-[28rem]">
                    <motion.div
                      className="relative w-full rounded-[40px] lg:rounded-[65px] overflow-hidden shadow-2xl border-2 border-purple-500/20"
                      style={{ aspectRatio: '9/19' }}
                      initial={{ x: isMobile ? 0 : 100, y: isMobile ? 40 : 0, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <img
                        src="/images/whatsTheNamePreview1.webp"
                        alt="Whats The Name App Store Preview Screenshot"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* WhatsTheName Banner Showcase */}
              <ScrollReveal>
                <div className="mb-16 lg:mb-24">
                  <div className="relative w-full aspect-[2/1] rounded-[24px] lg:rounded-[36px] overflow-hidden shadow-2xl border border-purple-500/20">
                    <img
                      src="/images/whats-the-name-banner.webp"
                      alt="Whats The Name App Banner"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </ScrollReveal>

              {/* Spibe Showcase */}
              <div className="mb-16 lg:mb-24">
                <div className="flex flex-col-reverse lg:flex-row gap-8 lg:gap-12 items-center lg:items-start">
                  {/* Video Showcase */}
                  <div className="flex-shrink-0 w-full sm:w-80 lg:w-96 xl:w-[28rem]">
                    <motion.div
                      className="relative w-full rounded-[40px] lg:rounded-[65px] overflow-hidden shadow-2xl"
                      style={{ aspectRatio: '9/19' }}
                      initial={{ x: isMobile ? 0 : -100, y: isMobile ? 40 : 0, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <LazyVideo
                        src="/videos/pairing_Spibe_demo.mp4"
                        poster="/images/SpibePreview.webp"
                        className="absolute inset-0 w-full h-full"
                        videoClassName="object-cover"
                      />
                    </motion.div>
                  </div>
                  {/* Text Content */}
                  <ScrollReveal direction="right">
                    <div className="flex-1 space-y-6">
                      <h3
                        ref={spibeRef}
                        className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-white font-bold"
                      >
                        Spibe - Past Project
                      </h3>

                      <div className="space-y-4 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-white">
                        <p>
                          Spibe was a past project centered on a conversation-starter platform
                          designed to match personalities and traits. Built around meaningful
                          interactions between friends—focusing on chatting, personal updates, and
                          intentional digital moments—I led the full front-end UI/UX design process,
                          defining how the app is navigated.
                        </p>

                        <p>
                          Over the past months, our team guided Spibe from initial concept to a
                          fully functional beta release through close collaboration with our
                          development team. We refined the UX, built primary communication features,
                          and created a custom multi-environment database.
                        </p>

                        <p>
                          Optimized for smooth onboarding and user retention, Spibe entered beta
                          testing, where real users explored its core features, provided feedback,
                          and helped shape our future work.
                        </p>
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              </div>

              {/* Spibe Desktop Screenshot */}
              <ScrollReveal>
                <div className="mb-16 lg:mb-24">
                  <div className="relative w-full aspect-[946/485] rounded-[24px] lg:rounded-[36px] overflow-hidden shadow-2xl border border-blue-500/15">
                    <img
                      src="/images/SpibeSparks.webp"
                      alt="Spibe Desktop Interface"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </ScrollReveal>

              {/* Prototypes Section */}
              <div className="mb-16 lg:mb-24" ref={prototypesRef}>
                <ScrollReveal>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl text-center text-white mb-12 lg:mb-16 font-bold">
                    Prototype Designs for Business Owners
                  </h2>
                </ScrollReveal>

                {/* ShakaCards Showcase */}
                <div className="mb-16 lg:mb-24">
                  <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start">
                    {/* Text Content */}
                    <ScrollReveal direction="left">
                      <div className="flex-1 space-y-6 lg:space-y-8">
                        <h3
                          ref={shakaCardsRef}
                          className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-white font-bold"
                        >
                          ShakaCards
                        </h3>

                        <p className="text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-white">
                          ShakaCards is our a digital card platform in works of moving forward
                          designed to bring a fresh, modern twist to how people share and interact
                          with cards. Built with simplicity and creativity in mind, ShakaCards lets
                          users send meaningful moments, celebrate milestones, and stay
                          connected—all through a smooth, intuitive experience. While we're keeping
                          our biggest innovation under wraps for now, ShakaCards introduces a unique
                          approach that will redefine what digital cards can be.
                        </p>

                        {/* Logo and Status */}
                        <div className="flex flex-col sm:flex-row flex-wrap items-center justify-start gap-6 lg:gap-6">
                          {/* ShakaCards Logo */}
                          <div className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-52 lg:h-52 rounded-[40px] overflow-hidden shadow-xl hover:scale-105 transition-transform duration-300">
                            <img
                              src="/images/shaka-app-logo.webp"
                              alt="ShakaCards Logo"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          </div>

                          {/* Launch Status Badge */}
                          <div className="relative">
                            <div
                              className="text-3xl sm:text-4xl lg:text-5xl text-center px-6 py-4 bg-clip-text font-bold"
                              style={{
                                WebkitTextFillColor: 'transparent',
                                backgroundImage:
                                  'conic-gradient(from 90deg, rgba(255, 45, 85, 1) 0%, rgba(242, 46, 120, 1) 2.5%, rgba(229, 47, 155, 1) 5%, rgba(203, 48, 224, 1) 10%, rgba(150, 67, 235, 1) 30%, rgba(97, 85, 245, 1) 50%, rgba(137, 75, 205, 1) 62.5%, rgba(176, 65, 165, 1) 75%, rgba(216, 55, 125, 1) 87.5%, rgba(255, 45, 85, 1) 100%)',
                                backgroundClip: 'text',
                              }}
                            >
                              Featured App
                              <br />
                              Prototype
                            </div>
                          </div>
                        </div>
                      </div>
                    </ScrollReveal>

                    {/* Video Showcase */}
                    <div className="flex-shrink-0 w-full sm:w-80 lg:w-96 xl:w-[28rem]">
                      <motion.div
                        className="relative w-full rounded-[40px] lg:rounded-[65px] overflow-hidden shadow-2xl"
                        style={{ aspectRatio: '9/19' }}
                        initial={{ x: isMobile ? 0 : 100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <LazyVideo
                          src="/videos/Shaka.mp4"
                          poster="/images/ShakaCardsDesign.webp"
                          className="absolute inset-0 w-full h-full"
                          videoClassName="object-cover object-right"
                        />
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Prototype Grid */}
                <div className="space-y-12 lg:space-y-16">
                  {/* Prototype 1 */}
                  <ScrollReveal direction="up">
                    <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
                      <motion.div
                        className="relative w-full sm:w-80 lg:w-96 aspect-[405/822] rounded-[40px] lg:rounded-[62px] overflow-hidden shadow-2xl"
                        initial={{ x: isMobile ? 0 : -100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/clashPreview.webp"
                          alt="Prototype Family Clash"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </motion.div>
                      <motion.div
                        className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-52 lg:h-52 rounded-[38px] overflow-hidden shadow-xl"
                        initial={{ x: isMobile ? 0 : 100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/clashLogo.webp"
                          alt="Prototype Family Clash Icon"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </motion.div>
                    </div>
                  </ScrollReveal>

                  {/* Prototype 2 */}
                  <ScrollReveal direction="up">
                    <div className="flex flex-col-reverse lg:flex-row gap-8 items-center justify-center">
                      <motion.div
                        className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-52 lg:h-52 rounded-[50px] overflow-hidden shadow-xl"
                        initial={{ x: isMobile ? 0 : -100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/petLogo.webp"
                          alt="Prototype Petconnect Social Icon"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </motion.div>
                      <motion.div
                        className="relative w-full sm:w-80 lg:w-96 aspect-[389/815] rounded-[40px] lg:rounded-[71px] overflow-hidden shadow-2xl"
                        initial={{ x: isMobile ? 0 : 100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/petPreview.webp"
                          alt="Prototype Petconnect Social"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </motion.div>
                    </div>
                  </ScrollReveal>

                  {/* Prototype 3 */}
                  <ScrollReveal direction="up">
                    <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
                      <motion.div
                        className="relative w-full sm:w-80 lg:w-96 aspect-[380/822] rounded-[40px] lg:rounded-[70px] overflow-hidden shadow-2xl"
                        initial={{ x: isMobile ? 0 : -100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/careerPreview.webp"
                          alt="Prototype Career Path"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </motion.div>
                      <motion.div
                        className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-52 lg:h-52 rounded-[50px] overflow-hidden shadow-xl"
                        initial={{ x: isMobile ? 0 : 100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/careerLogo.webp"
                          alt="Prototype Career Path Icon"
                          className="absolute inset-0 w-full h-full object-cover scale-125"
                        />
                      </motion.div>
                    </div>
                  </ScrollReveal>

                  {/* Prototype 4 (Affirmations) */}
                  <ScrollReveal direction="up" delay={0.3}>
                    <div className="flex flex-col-reverse lg:flex-row gap-8 items-center justify-center">
                      <motion.div
                        className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-52 lg:h-52 rounded-[50px] overflow-hidden shadow-xl"
                        initial={{ x: isMobile ? 0 : -100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/affirmationsLogo.webp"
                          alt="Prototype Affirmation Icon"
                          className="absolute inset-0 w-full h-full object-cover scale-[1.37] -translate-x-[18%] -translate-y-[10%] p-[0px] mx-[40px] my-[20px]"
                        />
                      </motion.div>
                      <motion.div
                        className="relative w-full sm:w-80 lg:w-96 aspect-[385/822] rounded-[40px] lg:rounded-[60px] overflow-hidden shadow-2xl"
                        initial={{ x: isMobile ? 0 : 100, y: isMobile ? 40 : 0, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <img
                          src="/images/affirmationsPreview.webp"
                          alt="Prototype Affirmations"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </motion.div>
                    </div>
                  </ScrollReveal>
                </div>
              </div>

              {/* Prototype Info Text */}
              <ScrollReveal>
                <div className="text-center max-w-4xl mx-auto space-y-6">
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-white font-bold">
                    Got a prototype you need me to design?
                  </h3>

                  <p className="text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed text-white">
                    To ensure your project is built the right way, I always begin with a custom
                    prototype tailored to your goals, timeline, and budget. Whether it's an
                    interactive game-night trivia app, a TikTok-style social platform for pets, or a
                    career-planning tool that helps students map out their future, a prototype
                    allows us to explore ideas visually before development begins. My quotes are
                    based on complexity, clarity, and the amount of work needed—designed to give you
                    the best value without expensive costs for basic templates. The result is a
                    clean, modern prototype that saves you time, money, and ensures your final
                    product is exactly what you envisioned.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-24 bg-zinc-900/50 relative z-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full" />
            </motion.div>

            <div className="space-y-2">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Custom Prototype Info */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-zinc-800/30">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal direction="up">
              <div className="bg-white/5 backdrop-blur-xl border-2 border-purple-600/50 rounded-3xl lg:rounded-[51px] p-8 sm:p-12 text-center">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                  Need a Custom Solution to produce Leads and Conversions?
                </h2>
                <p className="text-lg sm:text-xl text-stone-200 mb-8">
                  We create custom full applications and prototypes tailored to your specific goals,
                  timeline, and budget. We design solutions that save you time and money while
                  ensuring your final product matches your vision.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => router.push('/contact')}
                    className="px-8 py-4 bg-white text-purple-900 rounded-full hover:scale-105 transition-transform duration-300 font-semibold"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
        <ResponsiveFooter />
      </div>
    </div>
  );
}
